// src/components/AgeVerificationGate.tsx

import { useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import OnboardingProgressBar from "./OnboardingProgressBar";
import { getAmplifyCustomOutput } from "../lib/environment";

// ============================================================================
// Helpers (outside component — stable references, not recreated on render)
// ============================================================================

// Compress a File via the Canvas API and return a base64 data URL.
// Phone cameras (especially high-end Samsung / iPhone) produce 8–12 MB raw
// JPEGs that exceed both the 6 MB API Gateway sync-invocation payload limit
// and Rekognition's per-image bytes limit. Resizing to maxWidthPx and
// re-encoding at the given JPEG quality lands the output in roughly the
// 150–400 KB range while preserving enough detail for Rekognition's face
// and text detection. The returned string includes the
// `data:image/jpeg;base64,` prefix; the Lambda strips it before decoding.
async function compressImageToBase64(
  file: File,
  maxWidthPx: number = 1200,
  quality: number = 0.85,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // Preserve aspect ratio. Only downscale — never upscale a small image.
      let { width, height } = img;
      if (width > maxWidthPx) {
        height = Math.round((height * maxWidthPx) / width);
        width = maxWidthPx;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context unavailable'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // JPEG for consistent format and smaller size than PNG. The data URL
      // includes the `data:image/jpeg;base64,` prefix; Lambda strips it.
      const base64 = canvas.toDataURL('image/jpeg', quality);
      resolve(base64);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Image load failed'));
    };

    img.src = objectUrl;
  });
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function buildISODate(y?: number, m?: number, d?: number): string | null {
  if (!y || !m || !d) return null;
  const mm = String(m).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}

function checkAge18(isoDate?: string | null): boolean {
  if (!isoDate) return false;
  const birth = new Date(isoDate);
  if (isNaN(birth.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 18;
}

// ============================================================================
// Failure classification
// ============================================================================
//
// Lambda response shapes that this UI must distinguish:
//
//   HTTP 200 success:true                                → success
//   HTTP 200 success:false faceMatch:false               → face-match family
//                                                          (no ID face / no
//                                                          selfie face / below
//                                                          similarity threshold)
//   HTTP 200 success:false faceMatch:true (no attempts)  → underage
//   HTTP 200 success:false faceMatch:true + attempts:N   → OCR unreadable
//   HTTP 429 requiresSupport:true                        → face-match lockout
//   HTTP 400/413 with image-format/size body             → ID-zone hint
//   HTTP 400 with "Missing …" body                       → transient (generic)
//   HTTP 503                                             → transient (generic)
//   HTTP 500 / other !ok                                 → server_error
//
// LAMBDA_MSG mirrors the exact MSG strings from amplify/functions/verifyId/
// handler.ts so we can route 400/413 by body content (image-format hints go
// to the ID zone; everything else falls back to a generic transient message).
const LAMBDA_MSG = {
  noFaceOnId:
    "No face was detected on your ID. Make sure your full ID is visible and well-lit.",
  noFaceOnSelfie:
    "No face was detected in your selfie. Please look directly at the camera in good lighting.",
  faceMatchFailed:
    "Your selfie didn't match the ID photo. Please try again with better lighting.",
  invalidFormat:
    "We couldn't read that image format. Please take a new photo and try again.",
  imageTooLarge:
    "Your image is too large. Please use a smaller photo and try again.",
  underage:
    "Age verification failed. You must be 18 or older to use Crushhh.",
} as const;

type FailureClass =
  | { kind: 'success' }
  | { kind: 'id_zone'; message: string; isOcrUnreadable: boolean }
  | { kind: 'selfie_zone'; message: string }
  | { kind: 'underage' }
  | { kind: 'requires_support' }
  | { kind: 'transient' }
  | { kind: 'server_error' };

function classifyResponse(
  status: number,
  body: Record<string, unknown>,
  ocrAttemptsBeforeThis: number,
): FailureClass {
  // requiresSupport always wins — face-match lockout. Lambda emits at 429
  // today; defensive at 200 in case a future change moves it.
  if ((body as any).requiresSupport === true) {
    return { kind: 'requires_support' };
  }

  if (status === 429) {
    // 429 without requiresSupport not produced by the current Lambda.
    return { kind: 'transient' };
  }

  // 400 / 413: pick out the image-format / image-too-large bodies and route
  // those to the ID zone with a unified user-facing message. Other 400s
  // ("Missing request body" etc.) are generic transient errors.
  if (status === 400 || status === 413) {
    const errText = typeof (body as any).error === 'string' ? (body as any).error : '';
    if (errText === LAMBDA_MSG.invalidFormat || errText === LAMBDA_MSG.imageTooLarge) {
      return {
        kind: 'id_zone',
        message: "We couldn't read that image — please take a new photo of your ID.",
        isOcrUnreadable: false,
      };
    }
    return { kind: 'transient' };
  }

  if (status === 503) return { kind: 'transient' };

  if (status === 500 || status < 200 || status >= 300) {
    return { kind: 'server_error' };
  }

  // 2xx from here.
  if ((body as any).success === true) return { kind: 'success' };

  const faceMatch = (body as any).faceMatch === true;
  const attemptsRemaining = (body as any).attemptsRemaining;
  const errText = typeof (body as any).error === 'string' ? (body as any).error : '';

  if (!faceMatch) {
    // Face-match family. Map Lambda MSG → UI zone message.
    if (errText === LAMBDA_MSG.noFaceOnId) {
      return {
        kind: 'id_zone',
        message: "No face detected on your ID — make sure it's flat, fully in frame, and well lit.",
        isOcrUnreadable: false,
      };
    }
    if (errText === LAMBDA_MSG.noFaceOnSelfie) {
      return {
        kind: 'selfie_zone',
        message: "No face detected in your selfie — look directly at the camera in good lighting.",
      };
    }
    // Default face-match failure (similarity below threshold).
    return {
      kind: 'selfie_zone',
      message: "Your selfie didn't match the ID photo — please retake your selfie.",
    };
  }

  // faceMatch === true && success === false.
  // Lambda underage path returns no attemptsRemaining; OCR-unreadable path
  // always includes it (number, possibly 0). Use that to disambiguate.
  if (typeof attemptsRemaining !== 'number') {
    return { kind: 'underage' };
  }

  // OCR unreadable. Pick the user-facing message based on the upcoming
  // session count (count after this response is applied).
  const newCount = ocrAttemptsBeforeThis + 1;
  const message =
    newCount >= 3
      ? "Still having trouble reading your ID. Please enter your date of birth and ZIP code below."
      : "We had trouble reading your ID. Try again in better lighting with the ID flat and fully in frame.";
  return { kind: 'id_zone', message, isOcrUnreadable: true };
}

// ============================================================================
// Constants
// ============================================================================

const MONTHS = [
  { v: 1,  label: "January"   }, { v: 2,  label: "February" }, { v: 3,  label: "March"     },
  { v: 4,  label: "April"     }, { v: 5,  label: "May"       }, { v: 6,  label: "June"      },
  { v: 7,  label: "July"      }, { v: 8,  label: "August"    }, { v: 9,  label: "September" },
  { v: 10, label: "October"   }, { v: 11, label: "November"  }, { v: 12, label: "December"  },
];

const SUPPORT_EMAIL = "customersupport@crushhh.com";

// ============================================================================
// Component
// ============================================================================

interface AgeVerificationGateProps {
  onComplete: (zipcode: string) => Promise<void>;
}

const AgeVerificationGate = ({ onComplete }: AgeVerificationGateProps) => {
  const thisYear = new Date().getFullYear();

  const years = useMemo(() => {
    const arr: number[] = [];
    for (let y = thisYear - 18; y >= thisYear - 100; y--) arr.push(y);
    return arr;
  }, [thisYear]);

  const [idFrontFile, setIdFrontFile] = useState<File | null>(null);
  const [selfieFile,  setSelfieFile]  = useState<File | null>(null);
  const [birthYear,   setBirthYear]   = useState<number | "">("");
  const [birthMonth,  setBirthMonth]  = useState<number | "">("");
  const [birthDay,    setBirthDay]    = useState<number | "">("");
  const [zipcode,     setZipcode]     = useState("");
  const [loading,     setLoading]     = useState(false);

  // Per-zone error messages. Cleared independently when the corresponding
  // file changes. Either may be null while the other holds a message.
  const [idZoneError,     setIdZoneError]     = useState<string | null>(null);
  const [selfieZoneError, setSelfieZoneError] = useState<string | null>(null);

  // Errors that don't belong to a specific upload zone — underage,
  // requiresSupport, transient, server, pre-submit validation.
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Hard lockouts. Both disable submit; closing/reopening the gate clears
  // them along with the rest of component state.
  const [requiresSupport, setRequiresSupport] = useState(false);
  const [isUnderage,      setIsUnderage]      = useState(false);

  // Frontend session counter for OCR-unreadable responses. Increments on
  // each such response. NOT the DDB ocrAttempts field; that lives server-
  // side. Used only to drive the progressive-disclosure trigger and to
  // pick the right user-facing message in classifyResponse.
  const [frontendOcrAttempts, setFrontendOcrAttempts] = useState(0);

  // Progressive disclosure. Once frontendOcrAttempts hits 2, reveal the
  // birthdate + ZIP fields. Monotonic — once revealed, stays revealed.
  const [showFields, setShowFields] = useState(false);

  // animate-in latch: false on first paint, then flipped true on next frame
  // so the CSS transition has a from-state to animate from.
  const [fieldsAnimateIn, setFieldsAnimateIn] = useState(false);

  // Reveal trigger.
  useEffect(() => {
    if (frontendOcrAttempts >= 2 && !showFields) {
      setShowFields(true);
    }
  }, [frontendOcrAttempts, showFields]);

  // Animate-in: schedule the from→to transition on the next animation frame
  // so the initial render commits with opacity 0 / translateY(8px) before
  // the new style values arrive.
  useEffect(() => {
    if (!showFields) return;
    const id = requestAnimationFrame(() => setFieldsAnimateIn(true));
    return () => cancelAnimationFrame(id);
  }, [showFields]);

  // Per-zone error reset: a new ID photo clears the ID zone error and the
  // global underage / pre-submit messages. Same for selfie. requiresSupport
  // is intentionally not cleared — it reflects authoritative server state.
  useEffect(() => {
    setIdZoneError(null);
    setIsUnderage(false);
    setGlobalError(null);
  }, [idFrontFile]);

  useEffect(() => {
    setSelfieZoneError(null);
    setIsUnderage(false);
    setGlobalError(null);
  }, [selfieFile]);

  // touched: setter retained for any future visual variant; getter unused.
  const [, setTouched] = useState({
    birthMonth: false,
    birthDay:   false,
    birthYear:  false,
    zipcode:    false,
  });

  const maxDay = useMemo(() => {
    if (!birthYear || !birthMonth) return 31;
    return getDaysInMonth(Number(birthYear), Number(birthMonth));
  }, [birthYear, birthMonth]);

  const birthISO = buildISODate(
    birthYear  ? Number(birthYear)  : undefined,
    birthMonth ? Number(birthMonth) : undefined,
    birthDay   ? Number(birthDay)   : undefined,
  );

  const zipValid = /^\d{5}(?:-\d{4})?$/.test(zipcode.trim());

  // Once fields are revealed, they must be valid for submit. Before then,
  // the Lambda is responsible for OCR-extracting both — we don't gate.
  const canSubmit =
    !!idFrontFile &&
    !!selfieFile &&
    !loading &&
    !isUnderage &&
    !requiresSupport &&
    (showFields ? (!!birthISO && checkAge18(birthISO) && zipValid) : true);

  // --------------------------------------------------------------------------

  const handleApprove = async () => {
    // Reset all error state at the start of every submission attempt. Stale
    // messages from a previous attempt must never short-circuit a fresh one.
    setGlobalError(null);
    setIdZoneError(null);
    setSelfieZoneError(null);

    // Pre-submit validation only applies once fields are revealed. Before
    // that, missing birthdate/ZIP is fine — Lambda extracts via OCR.
    if (showFields) {
      if (!birthISO) {
        setGlobalError("Please select a complete birthdate.");
        return;
      }
      if (!checkAge18(birthISO)) {
        setGlobalError("You must be 18 or older to use Crushhh.");
        return;
      }
      if (!zipValid) {
        setGlobalError("Please enter a valid ZIP code (e.g., 12345 or 12345-6789).");
        return;
      }
    }
    if (!idFrontFile || !selfieFile) {
      setGlobalError("Please upload both your ID and selfie.");
      return;
    }

    const apiEndpoint = getAmplifyCustomOutput('PaymentAPIEndpoint');
    if (!apiEndpoint) {
      setGlobalError("Verification service not configured. Please contact support.");
      return;
    }

    setLoading(true);

    // Compress images client-side before base64 encoding. Phone-camera raw
    // files routinely exceed both API Gateway's 6 MB sync payload limit and
    // Rekognition's per-image bytes limit; 1200px @ JPEG 0.85 stays well
    // under both while preserving detail for face + text detection. Errors
    // here are local file issues, not Lambda issues — surface a zone-
    // specific message and abort before the timeout-protected fetch starts.
    let idFrontBase64: string;
    let selfieBase64: string;
    try {
      idFrontBase64 = await compressImageToBase64(idFrontFile, 1200, 0.85);
    } catch {
      setIdZoneError("We couldn't process that image. Please try a different photo.");
      setLoading(false);
      return;
    }
    try {
      selfieBase64 = await compressImageToBase64(selfieFile, 1200, 0.85);
    } catch {
      setSelfieZoneError("We couldn't process that image. Please try a different photo.");
      setLoading(false);
      return;
    }

    // 30-second client-side timeout. Rekognition + DynamoDB on a cold-start
    // Lambda can take 5–15s; 30s gives generous headroom without leaving
    // the user staring at a spinner forever if something stalls.
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 30000);

    try {
      const { tokens } = await fetchAuthSession({ forceRefresh: true });
      const idToken = tokens?.idToken?.toString();
      if (!idToken) throw new Error("Session expired. Please sign in again.");

      const response = await fetch(`${apiEndpoint}/verify-id`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          idFrontBase64,
          selfieBase64,
          // Only send manual fields when populated. Lambda treats absent as
          // "OCR will fill this in" — and absent on attempts 1–2 lets the
          // Lambda's ocrAttempts increment naturally.
          ...(birthISO          ? { manualBirthdate: birthISO }       : {}),
          ...(zipcode.trim()    ? { manualZipcode:   zipcode.trim() } : {}),
        }),
        signal: controller.signal,
      });

      // Tolerate non-JSON 5xx bodies — keeps the catch path simple.
      const result = await response.json().catch(() => ({} as Record<string, unknown>));
      const cls = classifyResponse(response.status, result, frontendOcrAttempts);

      switch (cls.kind) {
        case 'success': {
          const finalZipcode =
            (result as any).extractedData?.zipcode || zipcode.trim();
          await onComplete(finalZipcode);
          return;
        }

        case 'id_zone': {
          if (cls.isOcrUnreadable) {
            // Drives the reveal trigger: 2 → fields appear on next render.
            setFrontendOcrAttempts((n) => n + 1);
          }
          setIdZoneError(cls.message);
          return;
        }

        case 'selfie_zone': {
          setSelfieZoneError(cls.message);
          return;
        }

        case 'underage': {
          setIsUnderage(true);
          setGlobalError(
            (result as any).error ||
              "Age verification failed. You must be 18 or older to use Crushhh.",
          );
          return;
        }

        case 'requires_support': {
          setRequiresSupport(true);
          setGlobalError(
            "We weren't able to verify your identity after multiple attempts. Please contact support.",
          );
          return;
        }

        case 'transient': {
          setGlobalError("Verification is temporarily unavailable. Please try again in a moment.");
          return;
        }

        case 'server_error': {
          setGlobalError("Something went wrong. Please try again.");
          return;
        }
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        setGlobalError("Verification is taking longer than expected. Please try again.");
      } else {
        setGlobalError("Something went wrong. Please try again.");
      }
    } finally {
      window.clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // Styles — preserve the existing Crushhh design tokens (cream / ink /
  // coral / error). External skill file referenced in the brief was
  // unreachable on this filesystem, so the existing in-file token system
  // is the source of truth here.
  // --------------------------------------------------------------------------

  const COLOR_ERROR = "#C44B2E";
  const ERROR_RING  = `0 0 0 2px rgba(196, 75, 46, 0.55)`;

  const uploadBtnStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 48,
    height: 48,
    borderRadius: 12,
    border: "2.5px solid #1A1A1A",
    background: "#FFFFFF",
    color: "#1A1A1A",
    cursor: loading ? "not-allowed" : "pointer",
    opacity: loading ? 0.5 : 1,
    transition: "background 0.12s ease",
    flexShrink: 0,
  };

  const pillFieldStyle: React.CSSProperties = {
    width: "100%",
    border: "2.5px solid #E05A3A",
    borderRadius: 100,
    padding: "12px 18px",
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 15,
    background: "#FDF6EE",
    color: "#1A1A1A",
    outline: "none",
    boxSizing: "border-box",
    boxShadow: "none",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: 8,
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 15,
    fontWeight: 600,
    color: "#1A1A1A",
  };

  const helperStyle: React.CSSProperties = {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 13,
    color: "#888",
    marginTop: 8,
    marginBottom: 0,
    paddingLeft: 8,
  };

  const fileBadgeStyle: React.CSSProperties = {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 12,
    color: "#E05A3A",
    fontWeight: 700,
    marginTop: 6,
    marginBottom: 0,
  };

  const zoneMessageStyle: React.CSSProperties = {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 12,
    color: COLOR_ERROR,
    fontWeight: 600,
    marginTop: 8,
    marginBottom: 0,
    lineHeight: 1.4,
  };

  const submitPill = (disabled: boolean): React.CSSProperties => ({
    width: "100%",
    background: "#E05A3A",
    color: "#FFFFFF",
    border: "3px solid #1A1A1A",
    borderRadius: 100,
    boxShadow: "4px 4px 0px #1A1A1A",
    padding: "14px 36px",
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 18,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    boxSizing: "border-box",
    transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
  });

  // Wrapping div around an upload zone — gets a subtle red ring when the
  // zone is in error state. Padding adjusts so the ring sits clear of the
  // buttons; the transition keeps the appearance smooth.
  const uploadZoneWrapStyle = (hasError: boolean): React.CSSProperties => ({
    borderRadius: 14,
    padding: hasError ? 6 : 0,
    boxShadow: hasError ? ERROR_RING : "none",
    transition: "box-shadow 200ms ease, padding 200ms ease",
    boxSizing: "border-box",
  });

  const CameraIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const FolderIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  );

  // --------------------------------------------------------------------------

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: 16,
        background: "#F0DFC8",
        fontFamily: "'Nunito', sans-serif",
        boxSizing: "border-box",
      }}
    >
      {/* Processing overlay — same pattern as StandaloneSignUpForm /
          UserData. Covers the gate so the user sees only the Zodiyak
          spinner during the 30s window. The submit button underneath
          stays mounted (state-wise it's still disabled) but is visually
          occluded so the brief's "do not show both simultaneously" rule
          is satisfied via opacity occlusion. */}
      {loading && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <div className="p-8 m-4">
            <div
              className="animate-spin zodiyak-spinner rounded-full h-80 w-80"
              aria-hidden="true"
            />
          </div>
        </div>
      )}

      <div style={{ width: "100%", marginBottom: 48, maxWidth: 400 }}>
        <OnboardingProgressBar
          currentStep={1}
          steps={["Step 1: Age Verification", "Step 2: Phone Verification", "Step 3: Payment", "Done"]}
        />

        <div
          style={{
            background: "#FFFFFF",
            border: "3px solid #1A1A1A",
            borderRadius: 20,
            boxShadow: "6px 6px 0px #1A1A1A",
            padding: "28px 24px",
            boxSizing: "border-box",
            marginTop: 12,
          }}
        >
          <h2
            style={{
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 24,
              fontWeight: 700,
              color: "#1A1A1A",
              marginTop: 0,
              marginBottom: 8,
            }}
          >
            Verify Your Age
          </h2>
          <p
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: 13,
              color: "#555",
              lineHeight: 1.5,
              marginTop: 0,
              marginBottom: 24,
            }}
          >
            Ages 18+ for safety.<br />
            ID and photo for comparison, never stored.
          </p>

          {requiresSupport && (
            <div
              style={{
                marginBottom: 16,
                borderRadius: 12,
                border: "1.5px solid rgba(196, 75, 46, 0.4)",
                background: "rgba(255, 240, 235, 1)",
                padding: 12,
              }}
            >
              <p
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 13,
                  color: COLOR_ERROR,
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                We weren't able to verify your identity after multiple attempts.{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  style={{ color: COLOR_ERROR, textDecoration: "underline" }}
                >
                  Contact support
                </a>
                .
              </p>
            </div>
          )}

          {/* Underage takes the global block. No retry, no countdown, no
              support link — the Lambda copy is the only message. */}
          {isUnderage && globalError && (
            <div
              style={{
                marginBottom: 16,
                borderRadius: 12,
                border: "1.5px solid rgba(196, 75, 46, 0.4)",
                background: "rgba(255, 240, 235, 1)",
                padding: 12,
              }}
            >
              <p
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 13,
                  color: COLOR_ERROR,
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                {globalError}
              </p>
            </div>
          )}

          {/* Generic global errors — transient, server, pre-submit
              validation. Hidden when requiresSupport / underage own the
              message slot. */}
          {!requiresSupport && !isUnderage && globalError && (
            <div
              style={{
                marginBottom: 16,
                borderRadius: 12,
                border: "1.5px solid rgba(196, 75, 46, 0.4)",
                background: "rgba(255, 240, 235, 1)",
                padding: 12,
              }}
            >
              <p
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 13,
                  color: COLOR_ERROR,
                  margin: 0,
                }}
              >
                {globalError}
              </p>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* ID Front Upload */}
            <div>
              <label style={labelStyle}>Photo ID - Front</label>
              <div style={uploadZoneWrapStyle(!!idZoneError)}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    id="id-camera"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => setIdFrontFile(e.target.files?.[0] || null)}
                    disabled={loading}
                    className="hidden"
                  />
                  <label htmlFor="id-camera" title="Take Photo" style={uploadBtnStyle}>
                    <CameraIcon />
                  </label>

                  <input
                    id="id-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setIdFrontFile(e.target.files?.[0] || null)}
                    disabled={loading}
                    className="hidden"
                  />
                  <label htmlFor="id-upload" title="Upload File" style={uploadBtnStyle}>
                    <FolderIcon />
                  </label>
                  <div className="zodiyak-IDonly h-12 w-14 flex-shrink-0" aria-hidden="true"></div>
                </div>
              </div>
              {idFrontFile && <p style={fileBadgeStyle}>✓ {idFrontFile.name}</p>}
              {idZoneError && <p style={zoneMessageStyle}>{idZoneError}</p>}
            </div>

            {/* Selfie Upload */}
            <div>
              <label style={labelStyle}>Selfie</label>
              <div style={uploadZoneWrapStyle(!!selfieZoneError)}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    id="selfie-camera"
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={(e) => setSelfieFile(e.target.files?.[0] || null)}
                    disabled={loading}
                    className="hidden"
                  />
                  <label htmlFor="selfie-camera" title="Take Selfie" style={uploadBtnStyle}>
                    <CameraIcon />
                  </label>

                  <input
                    id="selfie-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setSelfieFile(e.target.files?.[0] || null)}
                    disabled={loading}
                    className="hidden"
                  />
                  <label htmlFor="selfie-upload" title="Upload File" style={uploadBtnStyle}>
                    <FolderIcon />
                  </label>
                  <div className="zodiyak-Selfie h-12 w-14 flex-shrink-0" aria-hidden="true"></div>
                </div>
              </div>
              {selfieFile && <p style={fileBadgeStyle}>✓ {selfieFile.name}</p>}
              {selfieZoneError && <p style={zoneMessageStyle}>{selfieZoneError}</p>}
            </div>

            {/* Progressive disclosure — birthdate + ZIP. Mounted only after
                two OCR-unreadable failures this session, then animates in
                on the next animation frame so the from-state has time to
                paint. Stays mounted once revealed. */}
            {showFields && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  opacity:    fieldsAnimateIn ? 1                : 0,
                  transform:  fieldsAnimateIn ? "translateY(0)" : "translateY(8px)",
                  transition: "opacity 300ms ease, transform 300ms ease",
                }}
              >
                <p
                  style={{
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: 13,
                    color: "#555",
                    margin: 0,
                    marginTop: 4,
                  }}
                >
                  Having trouble? Add these for verification:
                </p>

                {/* Birthdate */}
                <div>
                  <label style={labelStyle}>Birthdate</label>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                    <select
                      aria-label="Birth month"
                      value={birthMonth}
                      onFocus={() => setTouched((t) => ({ ...t, birthMonth: true }))}
                      onChange={(e) => {
                        const v = e.target.value ? Number(e.target.value) : "";
                        setBirthMonth(v as any);
                        if (v && birthYear && birthDay) {
                          const dmax = getDaysInMonth(Number(birthYear), Number(v));
                          if (Number(birthDay) > dmax) setBirthDay(dmax);
                        }
                      }}
                      style={{
                        ...pillFieldStyle,
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                      disabled={loading}
                    >
                      <option value="">Month</option>
                      {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                    </select>

                    <select
                      aria-label="Birth day"
                      value={birthDay}
                      onFocus={() => setTouched((t) => ({ ...t, birthDay: true }))}
                      onChange={(e) => setBirthDay(e.target.value ? Number(e.target.value) : ("" as any))}
                      style={{
                        ...pillFieldStyle,
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                      disabled={loading}
                    >
                      <option value="">Day</option>
                      {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>

                    <select
                      aria-label="Birth year"
                      value={birthYear}
                      onFocus={() => setTouched((t) => ({ ...t, birthYear: true }))}
                      onChange={(e) => {
                        const v = e.target.value ? Number(e.target.value) : "";
                        setBirthYear(v as any);
                        if (v && birthMonth && birthDay) {
                          const dmax = getDaysInMonth(Number(v), Number(birthMonth));
                          if (Number(birthDay) > dmax) setBirthDay(dmax);
                        }
                      }}
                      style={{
                        ...pillFieldStyle,
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                      disabled={loading}
                    >
                      <option value="">Year</option>
                      {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>

                {/* ZIP Code */}
                <div>
                  <label htmlFor="zipcode" style={labelStyle}>ZIP Code</label>
                  <input
                    id="zipcode"
                    type="text"
                    inputMode="numeric"
                    placeholder="12345 or 12345-6789"
                    value={zipcode}
                    onFocus={() => setTouched((t) => ({ ...t, zipcode: true }))}
                    onChange={(e) => setZipcode(e.target.value.replace(/[^\d-]/g, "").slice(0, 10))}
                    style={{
                      ...pillFieldStyle,
                      padding: "14px 20px",
                      fontSize: 16,
                      opacity: loading ? 0.6 : 1,
                      cursor: loading ? "not-allowed" : "text",
                    }}
                    disabled={loading}
                    required
                  />
                  <p style={helperStyle}>US ZIP only.</p>
                </div>
              </div>
            )}

            <button
              onClick={handleApprove}
              disabled={!canSubmit}
              style={{ ...submitPill(!canSubmit), marginTop: 12 }}
            >
              {loading ? "Verifying..." : "Approve & Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgeVerificationGate;
